import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import NavBar from '../../components/layout/NavBar'
import { deleteNotification, getNotification, getNotifications, markNotificationsRead, type NotificationFeedItem } from '../../api/messenger'

function formatRelative(v?: string | null) {
  const d = v ? new Date(v) : null
  if (!d || isNaN(d.getTime())) return ''
  const diff = Date.now() - d.getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d ago`
  return d.toLocaleDateString()
}

export default function SystemMessage() {
  const { notifId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const notifFromState = useMemo(() => (location.state as any)?.notif ?? null, [location.state])
  const [notif, setNotif] = useState<NotificationFeedItem | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [reply, setReply] = useState('')
  const [sent, setSent] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [sending, setSending] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    const load = async () => {
      if (!notifId) return
      setLoading(true)
      setError(null)
      try {
        if (notifFromState) {
          const hydrated: NotificationFeedItem = {
            id: String(notifFromState.id),
            type: notifFromState.type || notifFromState.event_type || 'system',
            verb: notifFromState.verb || notifFromState.title || 'Update',
            preview: notifFromState.preview || notifFromState.preview_text || '',
            actor: notifFromState.actor || null,
            deeplink: notifFromState.deeplink || null,
            postId: notifFromState.postId || notifFromState.post_id || null,
            targetId: notifFromState.targetId || notifFromState.target_id || null,
            targetType: notifFromState.targetType || notifFromState.target_type || null,
            createdAt: notifFromState.createdAt || notifFromState.created_at || new Date().toISOString(),
            read: Boolean(notifFromState.read),
            category: notifFromState.category || 'system',
            eventType: notifFromState.eventType || notifFromState.event_type || 'system',
            metadata: notifFromState.metadata || {},
            raw: notifFromState,
          }
          setNotif(hydrated)
          if (!hydrated.read) {
            void markNotificationsRead([hydrated.id]).catch(() => {})
          }
        } else {
          // Try detail endpoint first
          let n: NotificationFeedItem | null = null
          n = await getNotification(notifId)
          if (n) {
            setNotif(n)
            if (!n.read) void markNotificationsRead([n.id]).catch(() => {})
          } else {
            throw new Error('Notification not found')
          }
        }
      } catch (e: any) {
        setError(e?.response?.data?.detail || e?.message || 'Failed to load message')
        setNotif(null)
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [notifId, notifFromState, reloadKey])

  const onDelete = async () => {
    if (!notif) return
    // Skip API delete for mock placeholder
    if ((notif as any).metadata?.mock) {
      navigate('/inbox')
      return
    }
    setLoading(true)
    try {
      await deleteNotification(notif.id)
      navigate('/inbox')
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to remove message')
    } finally {
      setLoading(false)
    }
  }

  const rel = formatRelative(notif?.createdAt)
  const filteredMetadata = useMemo(() => {
    if (!notif?.metadata) return null
    const ignore = new Set([
      'deeplink_path',
      'deeplink',
      'path',
      'url',
      'link',
      'href',
      'icon',
      'image',
      'avatar',
      'thumbnail',
      'cover',
    ])
    const entries = Object.entries(notif.metadata).filter(([k, v]) => {
      if (ignore.has(k)) return false
      if (v == null) return false
      if (typeof v === 'object') return false
      return true
    })
    if (!entries.length) return null
    return Object.fromEntries(entries)
  }, [notif])

  const onSend = () => {
    // Mock reply send (no API yet)
    if (!reply.trim()) return
    setSending(true)
    setSent(false)
    setTimeout(() => {
      setSending(false)
      setSent(true)
      setTimeout(() => setSent(false), 2000)
    }, 800)
  }

  return (
    <div className="min-h-screen">
      <NavBar />
      <main className="mx-auto max-w-4xl px-4 py-6">
        <div className="mb-4 flex items-center gap-3">
          <Link to="/inbox" className="text-sm text-white/70 hover:text-white">← Back to Inbox</Link>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={onDelete}
              disabled={loading}
              className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold hover:bg-white/10 disabled:opacity-50"
            >
              Delete
            </button>
          </div>
        </div>

        {error && !notif && <div className="mb-3 text-sm text-red-400">{error}</div>}
        {error && !notif && (
          <div className="mb-4 rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="text-sm font-semibold text-red-300">{error}</div>
            <div className="text-xs text-white/60 mt-1">We couldn’t fetch this notification. Please try again.</div>
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => { setReloadKey((k) => k + 1); setError(null) }}
                className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold hover:bg-white/10"
              >
                Retry
              </button>
              <Link
                to="/inbox"
                className="rounded-full border border-white/10 bg-brand/90 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand"
              >
                Back to inbox
              </Link>
            </div>
          </div>
        )}
        {loading && !notif && (
          <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 to-white/[0.02] p-6 space-y-4 shadow-lg animate-pulse">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-white/10" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-32 bg-white/10 rounded" />
                <div className="h-3 w-24 bg-white/5 rounded" />
              </div>
            </div>
            <div className="h-3 w-64 bg-white/10 rounded" />
            <div className="h-24 w-full bg-white/5 rounded" />
          </div>
        )}
        {notif && (
          <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 to-white/[0.02] p-6 space-y-5 shadow-lg">
            <div className="flex items-start gap-4">
              {notif.actor?.avatarUrl ? (
                <img src={notif.actor.avatarUrl} className="h-12 w-12 rounded-full object-cover border border-white/20" />
              ) : (
                <div className="h-12 w-12 rounded-full bg-white/10 flex items-center justify-center text-base font-semibold text-white/85">
                  {(notif.actor?.name || 'System').toString().trim().charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-lg font-semibold truncate">{notif.actor?.name || 'System message'}</div>
                  <div className="text-xs text-white/60">{rel || ' '}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-2 py-0.5 text-[11px] font-bold uppercase text-white/80">
                    {notif.type || 'Message'}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-sm font-semibold">{notif.verb || 'Update'}</div>
              <div className="text-sm text-white/80 leading-relaxed">{notif.preview || 'No preview provided.'}</div>
            </div>

            {filteredMetadata && (
              <div className="rounded-lg border border-white/10 bg-white/5 p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold text-white/70">Details</div>
                  <button
                    type="button"
                    onClick={() => setShowDetails((v) => !v)}
                    className="text-[11px] text-white/60 hover:text-white"
                  >
                    {showDetails ? 'Hide' : 'Show'}
                  </button>
                </div>
                {showDetails && (
                  <pre className="text-xs text-white/70 whitespace-pre-wrap break-words">
                    {JSON.stringify(filteredMetadata, null, 2)}
                  </pre>
                )}
              </div>
            )}

            <div className="mt-4 space-y-2">
              <div className="text-sm font-semibold">Replies are disabled for system messages.</div>
              <div className="text-xs text-white/60">This notification is informational; no response is needed.</div>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
